import { mainEndpoints } from '@/lib/api/endpoints'
import { mainApi } from '@/lib/api/http'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'
import type { PageResult } from '@/lib/api/common'

import type {
  ChoiceGroup,
  ChoiceGroupDetail,
  CreateChoiceGroupBody,
  SaveChoiceGroupItemBody,
  UpdateChoiceGroupBody,
} from '../types'

const root = mainEndpoints.choiceGroups

/**
 * The tenant's choice groups, whole.
 *
 * Paged on the server but not here: reordering reads the array's index, so the
 * screen has to hold every group at once to move one of them.
 */
export async function fetchChoiceGroups(): Promise<ChoiceGroup[]> {
  const page = await mainApi.get<PageResult<ChoiceGroup>>(root, {
    params: { limit: PAGE_LIMIT_MAX, offset: 0 },
  })

  return page.rows ?? []
}

export async function fetchChoiceGroup(id: string): Promise<ChoiceGroupDetail> {
  return await mainApi.get<ChoiceGroupDetail>(`${root}/${id}`)
}

export async function createChoiceGroup(body: CreateChoiceGroupBody): Promise<ChoiceGroupDetail> {
  return await mainApi.post<ChoiceGroupDetail>(root, body)
}

export async function updateChoiceGroup(id: string, body: UpdateChoiceGroupBody): Promise<void> {
  await mainApi.put<void>(`${root}/${id}`, body)
}

export async function deleteChoiceGroup(id: string): Promise<void> {
  await mainApi.delete<void>(`${root}/${id}`)
}

/** Reorders the groups. The key is `itemIds`, not `ids` — categories differ here. */
export async function sortChoiceGroups(itemIds: string[]): Promise<void> {
  await mainApi.put<void>(`${root}/sort-order`, { itemIds })
}

/* -------------------------------------------------------------------------- */
/* Items                                                                       */
/* -------------------------------------------------------------------------- */

export async function createChoiceGroupItem(
  groupId: string,
  body: SaveChoiceGroupItemBody,
): Promise<void> {
  await mainApi.post<void>(`${root}/${groupId}/items`, body)
}

export async function updateChoiceGroupItem(
  groupId: string,
  itemId: string,
  body: SaveChoiceGroupItemBody,
): Promise<void> {
  await mainApi.put<void>(`${root}/${groupId}/items/${itemId}`, body)
}

export async function deleteChoiceGroupItem(groupId: string, itemId: string): Promise<void> {
  await mainApi.delete<void>(`${root}/${groupId}/items/${itemId}`)
}

export async function sortChoiceGroupItems(groupId: string, itemIds: string[]): Promise<void> {
  await mainApi.put<void>(`${root}/${groupId}/items/sort-order`, { itemIds })
}
